/**
 * Exports medical PA orders via the client CSV endpoint.
 * @module exportForClient
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";
import { throwApiError } from "../../throwApiError";
import { SearchForMedicalPaOrdersFromPostgresParams } from "./searchForMedicalPaOrdersFromPostgres";

export type { SearchForMedicalPaOrdersFromPostgresParams };

/**
 * Downloads a J-code-granularity CSV of medical PA orders.
 *
 * Sends the same body as `searchForMedicalPaOrdersFromPostgres` to
 * `medical/frontend-query/export-for-client`. That endpoint always returns CSV.
 *
 * @param params - Search criteria including query, facet filters, pagination, and date bounds.
 * @returns Raw CSV text from the backend.
 * @throws When the response is not OK (401, 403, 422, 500, and so on), or on a network error.
 */
export const exportForClient = async (
  params: SearchForMedicalPaOrdersFromPostgresParams,
): Promise<string> => {
  const bearerToken = await getBearerToken();

  const raw = JSON.stringify({
    org_id: params.org_id,
    query: params.query,
    facet_filters: params.facet_filters,
    hits_per_page: params.hits_per_page,
    page: params.page,
    created_at_start: params.created_at_start,
    created_at_end: params.created_at_end,
    date_of_service_start: params.date_of_service_start,
    date_of_service_end: params.date_of_service_end,
    date_of_work_start: params.date_of_work_start,
    date_of_work_end: params.date_of_work_end,
    ...(params.date_of_work_only !== undefined && {
      date_of_work_only: params.date_of_work_only,
    }),
    timezone_offset: params.timezone_offset ?? new Date().getTimezoneOffset(),
    ...(params.export !== undefined && { export: params.export }),
    ...(params.sort_by !== undefined && { sort_by: params.sort_by }),
    ...(params.sort_dir !== undefined && { sort_dir: params.sort_dir }),
    ...(params.default_sort !== undefined && {
      default_sort: params.default_sort,
    }),
  });

  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
    body: raw,
  };

  try {
    const response = await fetch(
      API_ENDPOINTS.EXPORT_FOR_CLIENT,
      requestOptions,
    );
    if (!response.ok) {
      await throwApiError(response);
    }
    const result = await response.text();
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
